import React from 'react';
import SEO from '../components/SEO';
import seoData from '../config/seoData';
import { Phone, MessageCircle, MapPin, CheckCircle, Wrench, Key, Shield, Zap, Award, Users, Instagram, ArrowRight, Clock, Package, Building2, Leaf } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate, Link } from 'react-router-dom';
import ContactForm from '../components/ContactForm';
import { generateFAQPage, generateService } from '../utils/structuredData';

const LocacaoLavadoraPiso = () => {
  const navigate = useNavigate();

  const whatsappNumber = "5531971823249";
  const whatsappMessage = encodeURIComponent("Olá! Vi a página sobre locação de lavadora de piso e gostaria de solicitar um orçamento.");

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
  };

  const handleCallClick = () => {
    window.location.href = 'tel:+5531971823249';
  };

  const benefits = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Economia Imediata",
      description: "Sem investimento inicial alto. Pague apenas pelo uso e economize capital."
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: "Manutenção Inclusa",
      description: "Todo suporte técnico e manutenção preventiva incluídos no contrato."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Equipamento Reserva",
      description: "Máquina backup disponível para não parar suas operações."
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Tecnologia Atualizada",
      description: "Equipamentos modernos das principais marcas do mercado."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Treinamento Completo",
      description: "Capacitação da equipe para operação segura e eficiente."
    },
    {
      icon: <Leaf className="w-8 h-8" />,
      title: "Sustentabilidade",
      description: "Redução no consumo de água e produtos químicos."
    }
  ];

  const equipmentTypes = [
    {
      title: "Lavadoras Pedestres",
      description: "Ideais para áreas de tráfego médio a intenso. Perfeitas para supermercados, lojas e escritórios.",
      applications: ["Supermercados", "Shopping Centers", "Hospitais", "Escolas"]
    },
    {
      title: "Lavadoras Tripuladas a Bateria",
      description: "Alta produtividade para grandes áreas. Zero emissões, operação silenciosa.",
      applications: ["Indústrias", "Centros de Distribuição", "Galpões", "Aeroportos"]
    },
    {
      title: "Lavadoras Tripuladas a GLP",
      description: "Máxima autonomia para operações contínuas em áreas externas e grandes espaços.",
      applications: ["Estacionamentos", "Áreas Externas", "Grandes Indústrias", "Logística"]
    },
    {
      title: "Varredeiras Industriais",
      description: "Limpeza a seco para remoção de detritos, pó e resíduos sólidos com alta eficiência.",
      applications: ["Fábricas", "Pátios", "Áreas de Produção", "Condomínios"]
    }
  ];

  const segments = [
    { icon: <Building2 className="w-6 h-6" />, name: "Hospitais e Clínicas" },
    { icon: <Package className="w-6 h-6" />, name: "Supermercados" },
    { icon: <Building2 className="w-6 h-6" />, name: "Shopping Centers" },
    { icon: <Building2 className="w-6 h-6" />, name: "Indústrias" },
    { icon: <Building2 className="w-6 h-6" />, name: "Condomínios" },
    { icon: <Building2 className="w-6 h-6" />, name: "Escolas e Universidades" },
    { icon: <Building2 className="w-6 h-6" />, name: "Hotéis e Resorts" },
    { icon: <Package className="w-6 h-6" />, name: "Centros Logísticos" }
  ];

  const faqItems = [
    {
      question: "Quanto custa alugar uma lavadora de piso?",
      answer: "O custo varia conforme o modelo, período de locação e serviços inclusos. Em média, a locação mensal pode variar de R$ 800 a R$ 3.500, dependendo do tipo de equipamento (pedestre, tripulada a bateria ou GLP). A WellTec oferece planos flexíveis com manutenção inclusa. Solicite um orçamento personalizado via WhatsApp."
    },
    {
      question: "A manutenção está incluída no aluguel?",
      answer: "Sim! Todos os planos de locação da WellTec incluem manutenção preventiva e corretiva, peças de reposição, suporte técnico especializado e equipamento reserva em caso de necessidade. Você paga um valor fixo mensal e não precisa se preocupar com custos adicionais de manutenção."
    },
    {
      question: "Qual o prazo mínimo de locação?",
      answer: "A WellTec oferece planos flexíveis a partir de 30 dias. Também atendemos locações pontuais para eventos, reformas ou limpezas pós-obra. Para projetos de longo prazo (6, 12 ou 24 meses), oferecemos condições especiais e descontos progressivos."
    },
    {
      question: "Vocês atendem toda região de Belo Horizonte?",
      answer: "Sim! A WellTec atende Belo Horizonte, Contagem, Betim, Nova Lima, Mário Campos e toda a região metropolitana de BH. Oferecemos entrega, instalação, treinamento e suporte técnico local com atendimento rápido em até 24 horas."
    },
    {
      question: "Qual a diferença entre lavadora pedestre e tripulada?",
      answer: "Lavadoras pedestres são operadas a pé, ideais para áreas de até 2.000 m² como supermercados e hospitais. Lavadoras tripuladas (ride-on) possuem assento para o operador, são mais produtivas e indicadas para áreas acima de 2.000 m² como indústrias, centros de distribuição e grandes galpões. A escolha depende do tamanho da área e da frequência de limpeza."
    }
  ];

  const processSteps = [
    { number: "1", title: "Análise de Necessidade", description: "Avaliamos seu espaço e tipo de piso para recomendar o equipamento ideal." },
    { number: "2", title: "Orçamento Personalizado", description: "Elaboramos proposta sob medida com valores transparentes e sem pegadinhas." },
    { number: "3", title: "Entrega e Treinamento", description: "Instalamos o equipamento e treinamos sua equipe para operação segura." },
    { number: "4", title: "Suporte Contínuo", description: "Manutenção preventiva, peças e assistência técnica durante todo o contrato." }
  ];

  // Structured Data
  const serviceStructuredData = generateService(
    "Locação de Lavadoras de Piso Industriais",
    "Locação mensal de lavadoras e varredeiras de piso industriais com manutenção inclusa, treinamento e suporte técnico em Belo Horizonte e região metropolitana.",
    ["Belo Horizonte", "Contagem", "Betim", "Nova Lima", "Mário Campos"]
  );

  const faqStructuredData = generateFAQPage(
    faqItems.map(item => ({
      question: item.question,
      answer: item.answer
    }))
  );

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={seoData['/locacao-lavadora-piso'].title}
        description={seoData['/locacao-lavadora-piso'].description}
        canonical={seoData['/locacao-lavadora-piso'].canonical}
        ogImage="https://images.squarespace-cdn.com/content/v1/6944bce717735870c92588a9/6c6006e7-0128-4018-adb3-c63a42fece8d/IMG-20250626-WA0092.jpg"
      />

      {/* Fixed WhatsApp Button */}
      <div className="fixed bottom-6 right-6 z-50 animate-bounce-slow">
        <Button
          onClick={handleWhatsAppClick}
          className="bg-green-500 hover:bg-green-600 text-white rounded-full w-16 h-16 shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110"
        >
          <MessageCircle className="w-8 h-8" />
        </Button>
      </div>

      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
              alt="WellTec Logo" 
              className="h-16 w-auto"
            />
          </div>
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition-colors">Início</Link>
            <Link to="/servicos" className="hover:text-blue-400 transition-colors">Serviços</Link>
            <Link to="/equipamentos-disponiveis" className="hover:text-blue-400 transition-colors">Catálogo</Link>
            <Link to="/limpeza-pos-obra" className="hover:text-green-400 transition-colors">Limpeza Pós-Obra</Link>
            <Link to="/locacao-lavadora-piso" className="text-blue-400 font-semibold">Locação</Link>
            <Link to="/blog" className="hover:text-blue-400 transition-colors">Blog</Link>
            <a 
              href="https://www.instagram.com/welltecbrasil/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-blue-400 transition-colors"
              title="Instagram @welltecbrasil"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="relative bg-slate-900 text-white py-24 md:py-32 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.85), rgba(30, 58, 138, 0.75)), url('https://images.unsplash.com/photo-1771530789155-b1f03fbf82b5?w=1600')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Locação de Lavadora de Piso em Belo Horizonte
            </h1>
            <p className="text-base md:text-lg mb-8 text-gray-200">
              Aluguel mensal de lavadoras e varredeiras industriais com <strong>manutenção inclusa</strong>, equipamento reserva e suporte técnico especializado. Atendimento em todo estado de Minas Gerais, incluindo região metropolitana de Belo Horizonte e demais localidades.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={handleWhatsAppClick}
                size="lg"
                className="bg-green-500 hover:bg-green-600 text-white text-lg px-8 py-6 transition-all duration-300 hover:scale-105"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Solicitar Orçamento Grátis
              </Button>
              <Button
                onClick={handleCallClick}
                size="lg"
                variant="outline"
                className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white hover:text-slate-900 text-lg px-8 py-6 transition-all duration-300"
              >
                <Phone className="w-5 h-5 mr-2" />
                (31) 97182-3249
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* GEO Block 50 palavras - O que é Locação de Lavadora de Piso */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 text-center">
              O que é Locação de Lavadora de Piso?
            </h2>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <p className="text-base text-gray-700 leading-relaxed">
                A <strong>locação de lavadora de piso</strong> é o aluguel mensal de equipamentos industriais de limpeza, incluindo lavadoras pedestres, tripuladas (a bateria ou GLP) e varredeiras. O serviço inclui manutenção preventiva e corretiva, treinamento operacional, peças de reposição e suporte técnico especializado. É uma solução econômica que elimina investimento inicial alto e garante tecnologia atualizada sem custos de propriedade.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Por que Alugar ao Invés de Comprar?
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
              A locação oferece vantagens estratégicas e financeiras que tornam sua operação mais eficiente e econômica
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-slate-200 hover:border-blue-500 transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-6">
                  <div className="bg-blue-600 w-16 h-16 rounded-lg flex items-center justify-center mb-4 text-white">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-slate-900">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* GEO Block 100 palavras - Tipos de Equipamentos */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-center">
              Tipos de Equipamentos Disponíveis para Locação
            </h2>
            <div className="bg-slate-800 p-8 rounded-lg mb-12">
              <p className="text-base text-gray-300 leading-relaxed">
                A WellTec oferece locação de <strong>lavadoras pedestres</strong> (walk-behind) para áreas de até 2.000 m², ideais para supermercados e hospitais; <strong>lavadoras tripuladas a bateria</strong> (ride-on elétricas) com zero emissões para indústrias e centros logísticos; <strong>lavadoras tripuladas a GLP</strong> com máxima autonomia para grandes galpões e áreas externas; e <strong>varredeiras industriais</strong> para limpeza a seco de detritos e pó. Todos os equipamentos são de marcas líderes de mercado, revisados e com manutenção preventiva garantida. A escolha do modelo ideal depende do tamanho da área, tipo de piso, frequência de limpeza e ambiente (interno ou externo). Nossa equipe técnica realiza análise gratuita no local.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {equipmentTypes.map((equipment, index) => (
                <Card key={index} className="bg-slate-800 border-slate-700 hover:border-blue-500 transition-all duration-300">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-white mb-3">{equipment.title}</h3>
                    <p className="text-sm text-gray-300 mb-4">{equipment.description}</p>
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-blue-400">APLICAÇÕES:</p>
                      <div className="flex flex-wrap gap-2">
                        {equipment.applications.map((app, idx) => (
                          <span key={idx} className="text-xs bg-blue-600 text-white px-3 py-1 rounded-full">
                            {app}
                          </span>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Target Segments Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Atendemos Todos os Segmentos
            </h2>
            <p className="text-base md:text-lg mb-10 text-gray-100">
              Soluções personalizadas de locação para cada tipo de negócio
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {segments.map((segment, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm p-4 rounded-lg hover:bg-white/20 transition-all duration-300">
                  <div className="flex flex-col items-center">
                    <div className="text-white mb-2">{segment.icon}</div>
                    <p className="text-xs text-center font-semibold">{segment.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GEO Block 150 palavras - Processo de Locação */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 text-center">
              Como Funciona o Processo de Locação?
            </h2>
            <div className="bg-gray-50 p-8 rounded-lg mb-12">
              <p className="text-base text-gray-700 leading-relaxed mb-6">
                O processo de <strong>locação de lavadoras de piso na WellTec</strong> é simples, transparente e sem burocracia. Primeiro, realizamos uma <strong>análise técnica gratuita</strong> no local para identificar o tipo de piso, tamanho da área, frequência de limpeza necessária e particularidades do ambiente. Com base nisso, recomendamos o equipamento ideal (pedestre, tripulada a bateria ou GLP). Em seguida, elaboramos um <strong>orçamento personalizado</strong> com valores fixos mensais, sem taxas ocultas. Após aprovação, agendamos a entrega, instalação e <strong>treinamento operacional completo</strong> da sua equipe, ensinando técnicas de manuseio, segurança e limpeza eficiente. Durante todo o contrato, oferecemos <strong>manutenção preventiva programada</strong>, atendimento corretivo em até 24 horas, reposição de peças e equipamento reserva quando necessário. Nosso time técnico está sempre disponível via WhatsApp, telefone ou visita presencial. Contratos flexíveis a partir de 30 dias, com renovação automática e sem multa de cancelamento.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              {processSteps.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section (Inline) */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Perguntas Frequentes sobre Locação
              </h2>
              <p className="text-base md:text-lg text-gray-600">
                Tire suas dúvidas sobre aluguel de lavadoras de piso
              </p>
            </div>
            <div className="space-y-6">
              {faqItems.map((faq, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="text-lg font-bold text-slate-900 mb-3">{faq.question}</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Button
                onClick={() => navigate('/faq-locacao-lavadora-piso')}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Ver Todas as Perguntas Frequentes
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area with GEO targeting */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <MapPin className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Atendimento em Todo Estado de Minas Gerais
          </h2>
          <p className="text-base md:text-lg mb-6">
            Locação de lavadoras de piso com entrega, instalação e suporte técnico especializado em todo MG.
            <br />
            <strong>Atendimento na região metropolitana de Belo Horizonte:</strong>
          </p>
          <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto mb-6">
            {['Belo Horizonte', 'Contagem', 'Betim', 'Nova Lima', 'Mário Campos', 'Ibirité', 'Ribeirão das Neves', 'Santa Luzia'].map((city, index) => (
              <span key={index} className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold">
                {city}
              </span>
            ))}
          </div>
          <p className="text-sm text-gray-100 max-w-2xl mx-auto">
            Também atendemos outras localidades de Minas Gerais. Entre em contato para consultar disponibilidade na sua região.
          </p>
        </div>
      </section>

      {/* Páginas por Localidade e Segmento */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Locação por Localidade e Segmento
              </h2>
              <p className="text-base md:text-lg text-gray-600">
                Páginas específicas com informações detalhadas para sua região e tipo de negócio
              </p>
            </div>

            {/* Localidades */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                <MapPin className="w-6 h-6 text-blue-600" />
                Locação por Cidade
              </h3>
              <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
                <button 
                  onClick={() => navigate('/locacao-lavadora-piso-belo-horizonte')}
                  className="bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 hover:border-blue-500 p-4 rounded-lg transition-all duration-300 text-left group"
                >
                  <p className="text-sm font-bold text-blue-900 group-hover:text-blue-600">Belo Horizonte</p>
                  <p className="text-xs text-gray-600 mt-1">Ver página →</p>
                </button>
                <button 
                  onClick={() => navigate('/locacao-lavadora-piso-contagem')}
                  className="bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 hover:border-blue-500 p-4 rounded-lg transition-all duration-300 text-left group"
                >
                  <p className="text-sm font-bold text-blue-900 group-hover:text-blue-600">Contagem</p>
                  <p className="text-xs text-gray-600 mt-1">Ver página →</p>
                </button>
                <button 
                  onClick={() => navigate('/locacao-lavadora-piso-betim')}
                  className="bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 hover:border-blue-500 p-4 rounded-lg transition-all duration-300 text-left group"
                >
                  <p className="text-sm font-bold text-blue-900 group-hover:text-blue-600">Betim</p>
                  <p className="text-xs text-gray-600 mt-1">Ver página →</p>
                </button>
                <button 
                  onClick={() => navigate('/locacao-lavadora-piso-nova-lima')}
                  className="bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 hover:border-blue-500 p-4 rounded-lg transition-all duration-300 text-left group"
                >
                  <p className="text-sm font-bold text-blue-900 group-hover:text-blue-600">Nova Lima</p>
                  <p className="text-xs text-gray-600 mt-1">Ver página →</p>
                </button>
                <button 
                  onClick={() => navigate('/locacao-lavadora-piso-minas-gerais')}
                  className="bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 hover:border-blue-500 p-4 rounded-lg transition-all duration-300 text-left group"
                >
                  <p className="text-sm font-bold text-blue-900 group-hover:text-blue-600">Minas Gerais</p>
                  <p className="text-xs text-gray-600 mt-1">Ver página →</p>
                </button>
              </div>
            </div>

            {/* Segmentos */}
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                <Building2 className="w-6 h-6 text-blue-600" />
                Locação por Segmento
              </h3>
              <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
                <button 
                  onClick={() => navigate('/locacao-lavadora-piso-hospital')}
                  className="bg-green-50 hover:bg-green-100 border-2 border-green-200 hover:border-green-500 p-4 rounded-lg transition-all duration-300 text-left group"
                >
                  <p className="text-sm font-bold text-green-900 group-hover:text-green-600">Hospitais</p>
                  <p className="text-xs text-gray-600 mt-1">Ver página →</p>
                </button>
                <button 
                  onClick={() => navigate('/locacao-lavadora-piso-supermercado')}
                  className="bg-green-50 hover:bg-green-100 border-2 border-green-200 hover:border-green-500 p-4 rounded-lg transition-all duration-300 text-left group"
                >
                  <p className="text-sm font-bold text-green-900 group-hover:text-green-600">Supermercados</p>
                  <p className="text-xs text-gray-600 mt-1">Ver página →</p>
                </button>
                <button 
                  onClick={() => navigate('/locacao-lavadora-piso-condominio')}
                  className="bg-green-50 hover:bg-green-100 border-2 border-green-200 hover:border-green-500 p-4 rounded-lg transition-all duration-300 text-left group"
                >
                  <p className="text-sm font-bold text-green-900 group-hover:text-green-600">Condomínios</p>
                  <p className="text-xs text-gray-600 mt-1">Ver página →</p>
                </button>
                <button 
                  onClick={() => navigate('/locacao-lavadora-piso-industria')}
                  className="bg-green-50 hover:bg-green-100 border-2 border-green-200 hover:border-green-500 p-4 rounded-lg transition-all duration-300 text-left group"
                >
                  <p className="text-sm font-bold text-green-900 group-hover:text-green-600">Indústrias</p>
                  <p className="text-xs text-gray-600 mt-1">Ver página →</p>
                </button>
                <button 
                  onClick={() => navigate('/locacao-lavadora-piso-shopping')}
                  className="bg-green-50 hover:bg-green-100 border-2 border-green-200 hover:border-green-500 p-4 rounded-lg transition-all duration-300 text-left group"
                >
                  <p className="text-sm font-bold text-green-900 group-hover:text-green-600">Shopping Centers</p>
                  <p className="text-xs text-gray-600 mt-1">Ver página →</p>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Seção de Link para Catálogo */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 md:p-12 border-2 border-blue-200 shadow-lg">
              <div className="flex items-start gap-4">
                <Package className="w-10 h-10 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    Conheça Nosso Catálogo de Equipamentos
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    Consulte o catálogo técnico completo das lavadoras e varredeiras de piso disponíveis para locação. Veja especificações detalhadas, folhetos técnicos e compare os modelos para encontrar o equipamento ideal para sua operação.
                  </p>
                  <Link 
                    to="/equipamentos-disponiveis"
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors shadow-md hover:shadow-lg"
                  >
                    Consultar Catálogo de Equipamentos
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="py-24 relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 58, 138, 0.9), rgba(15, 23, 42, 0.9)), url('https://images.unsplash.com/photo-1770303430514-6ec150a21383?w=1600')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Solicite um Orçamento de Locação Agora
          </h2>
          <p className="text-lg md:text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Atendimento rápido, orçamento sem compromisso e análise técnica gratuita no local
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleWhatsAppClick}
              size="lg"
              className="bg-green-500 hover:bg-green-600 text-white text-xl px-12 py-8 transition-all duration-300 hover:scale-110 shadow-2xl"
            >
              <MessageCircle className="w-6 h-6 mr-3" />
              Falar com Especialista
            </Button>
            <Button
              onClick={handleCallClick}
              size="lg"
              variant="outline"
              className="bg-white text-slate-900 hover:bg-gray-100 text-xl px-12 py-8"
            >
              <Phone className="w-6 h-6 mr-3" />
              Ligar Agora
            </Button>
          </div>
        </div>
      </section>

      {/* Formulário de Contato */}
      <ContactForm />

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
                  alt="WellTec Logo" 
                  className="h-20 w-auto"
                />
              </div>
              <p className="text-gray-400 mb-4">
                Especialistas em locação e manutenção de lavadoras de piso industriais.
              </p>
              <a 
                href="https://www.instagram.com/welltecbrasil/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
              >
                <Instagram className="w-5 h-5" />
                <span>@welltecbrasil</span>
              </a>
            </div>
            <div>
              <h5 className="text-lg font-bold mb-4">Links Rápidos</h5>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors">Início</Link></li>
                <li><Link to="/servicos" className="text-gray-400 hover:text-white transition-colors">Serviços</Link></li>
                <li><Link to="/locacao-lavadora-piso" className="text-blue-400 hover:text-blue-300 transition-colors">Locação</Link></li>
                <li><Link to="/faq-locacao-lavadora-piso" className="text-gray-400 hover:text-white transition-colors">FAQ</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors">Blog</Link></li>
                <li></li>
                <li><a href="/#contato" className="text-gray-400 hover:text-white transition-colors">Contato</a></li>
              </ul>
            </div>
            <div>
              <h5 className="text-lg font-bold mb-4">Contato</h5>
              <ul className="space-y-2 text-gray-400">
                <li>(31) 97182-3249</li>
                <li>Mário Campos, MG</li>
                <li>Seg-Sex: 08:00-18:00</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-gray-400">
            <p>&copy; 2025 WellTec - Locações e Serviços Técnicos. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LocacaoLavadoraPiso;